import React from 'react';
import { PolicyPage, Section, Callout, PolicyTable } from './PolicyLayout';

const RELATED = [
  { label: 'Privacy Policy',      path: '/privacy'      },
  { label: 'Terms of Service',    path: '/terms'        },
  { label: 'Cookie Policy',       path: '/cookies'      },
  { label: 'Cancellation Policy', path: '/cancellation' },
];

const SECTIONS = [
  { id: 'overview',   label: 'Overview'            },
  { id: 'collection', label: 'Data We Collect'     },
  { id: 'use',        label: 'How We Use It'       },
  { id: 'sharing',    label: 'Sharing & Disclosure'},
  { id: 'retention',  label: 'Data Retention'      },
  { id: 'rights',     label: 'Your Rights'         },
  { id: 'security',   label: 'Security'            },
  { id: 'children',   label: "Children's Privacy"  },
  { id: 'contact',    label: 'Contact Us'          },
];

const GOLD = '#B8975A';

const PrivacyPolicy = () => (
  <PolicyPage
    badge="Legal · Privacy"
    title="Privacy Policy"
    subtitle="We are committed to protecting your personal information and your right to privacy. This policy explains what information we collect, how we use it, and what rights you have."
    updated="January 15, 2026"
    heroImage="https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1400&q=80"
    sections={SECTIONS}
    relatedPages={RELATED}
    currentPage="/privacy"
  >

    <Section id="overview" label="Introduction" title="Our Commitment to Your Privacy">
      <p className="policy-p">
        Vole Luxe Travel & Safaris ("we", "us", or "our") operates the voleluxe.co.tz website and related services. This Privacy Policy describes how we collect, use, process, and disclose your information when you use our platform, book a tour, or interact with us in any capacity.
      </p>
      <Callout>
        <strong>Plain English summary:</strong> We collect only the information necessary to provide you with an exceptional tour experience. We never sell your personal data to third parties. You have the right to access, correct, or delete your information at any time.
      </Callout>
      <p className="policy-p">
        By using our services, you agree to the collection and use of information as described in this policy. We will notify you of any material changes by updating the "Last Updated" date at the top of this page and, where appropriate, by sending you a direct notification.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="collection" label="Data Collection" title="Information We Collect">
      <p className="policy-p">
        We collect information in three ways: information you give us directly, information collected automatically when you use our services, and information from third-party sources.
      </p>
      <p className="policy-p"><strong>Information you provide directly:</strong></p>
      <ul className="policy-ul">
        <li>Full name, email address, and phone number when creating an account or making a booking</li>
        <li>Billing and payment information (processed securely via our payment provider — we do not store card details)</li>
        <li>Passport details or government-issued ID when required for tour access to restricted historical sites</li>
        <li>Dietary requirements, accessibility needs, and medical information you voluntarily share to help us accommodate you</li>
        <li>Correspondence — emails, messages, and survey responses you send us</li>
        <li>Reviews and testimonials you submit through our platform</li>
      </ul>
      <p className="policy-p"><strong>Information collected automatically:</strong></p>
      <ul className="policy-ul">
        <li>Device information: browser type, operating system, unique device identifiers</li>
        <li>Usage data: pages visited, time on page, links clicked, referring URLs</li>
        <li>IP address and approximate geographic location (country and city level)</li>
        <li>Cookie data — see our <a href="/cookies" style={{ color: GOLD }}>Cookie Policy</a> for full details</li>
      </ul>
    </Section>

    <div className="section-divider" />

    <Section id="use" label="Data Usage" title="How We Use Your Information">
      <p className="policy-p">
        We use the information we collect for legitimate business purposes, all of which are directly related to providing you with outstanding service.
      </p>

      {/* PolicyTable adds horizontal scroll + shadow-hint on mobile */}
      <PolicyTable>
        <thead>
          <tr>
            <th>Purpose</th>
            <th>Legal Basis</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Booking & Tour Delivery', 'Contract performance',  'Processing reservations, sending confirmations, coordinating guides'],
            ['Customer Support',        'Contract performance',  'Responding to enquiries, resolving issues, managing changes'],
            ['Payment Processing',      'Contract performance',  'Charging for bookings, processing refunds'],
            ['Safety & Security',       'Legitimate interest',   'Preventing fraud, detecting abuse, protecting our platform'],
            ['Marketing',               'Consent',               'Sending newsletters and offers — only with your explicit permission'],
            ['Analytics',               'Legitimate interest',   'Understanding how our service is used to improve it'],
            ['Legal Compliance',        'Legal obligation',      'Meeting tax, financial, and regulatory requirements'],
          ].map(([purpose, basis, detail], i) => (
            <tr key={i}>
              <td><strong>{purpose}</strong></td>
              <td>{basis}</td>
              <td>{detail}</td>
            </tr>
          ))}
        </tbody>
      </PolicyTable>
    </Section>

    <div className="section-divider" />

    <Section id="sharing" label="Third Parties" title="Sharing & Disclosure">
      <p className="policy-p">
        We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following limited circumstances:
      </p>
      <ul className="policy-ul">
        <li><strong>Service providers:</strong> Carefully vetted companies that help us operate — payment processors, email delivery, cloud hosting, and analytics platforms. All are bound by data processing agreements.</li>
        <li><strong>Tour guides and partners:</strong> We share your name and booking details with the assigned guide. We share dietary or medical information only when relevant to your safety or comfort on the tour.</li>
        <li><strong>Legal requirements:</strong> When required by law, court order, or legitimate government request.</li>
        <li><strong>Business transfer:</strong> In the event of a merger or acquisition, your information may be transferred. You will be notified in advance and given the opportunity to delete your account.</li>
      </ul>
      <Callout>
        <strong>We never sell your data.</strong> No advertiser, data broker, or unrelated third party will ever receive your personal information.
      </Callout>
    </Section>

    <div className="section-divider" />

    <Section id="retention" label="Data Retention" title="How Long We Keep Your Data">
      <p className="policy-p">
        We retain your personal information for as long as necessary to fulfil the purposes outlined in this policy, unless a longer retention period is required by law.
      </p>
      <ul className="policy-ul">
        <li><strong>Account data:</strong> Retained for the lifetime of your account plus 3 years after closure</li>
        <li><strong>Booking records:</strong> 7 years for financial and tax compliance purposes</li>
        <li><strong>Communications:</strong> 3 years from the date of last correspondence</li>
        <li><strong>Marketing data:</strong> Until you withdraw consent or request deletion</li>
        <li><strong>Analytics data:</strong> Aggregated, anonymised — retained indefinitely</li>
      </ul>
      <p className="policy-p">
        You may request deletion of your personal data at any time. We will action deletion requests within 30 days, except where retention is required by law.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="rights" label="Your Rights" title="Control Over Your Data">
      <p className="policy-p">
        Depending on your location, you may have rights under GDPR, CCPA, or other applicable privacy legislation. We honour all of the following regardless of where you are based.
      </p>
      <ul className="policy-ul">
        <li><strong>Right of access:</strong> Request a copy of all personal data we hold about you</li>
        <li><strong>Right to rectification:</strong> Correct inaccurate or incomplete information</li>
        <li><strong>Right to erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
        <li><strong>Right to restriction:</strong> Ask us to limit how we process your data</li>
        <li><strong>Right to portability:</strong> Receive your data in a structured, machine-readable format</li>
        <li><strong>Right to object:</strong> Object to processing based on legitimate interest, including marketing</li>
        <li><strong>Right to withdraw consent:</strong> Where processing is based on consent, withdraw it at any time</li>
      </ul>
      <p className="policy-p">
        To exercise any of these rights, contact our Privacy Team at <strong>privacy@voleluxe.co.tz</strong>. We will respond within 30 days. There is no charge for requests unless they are manifestly unfounded or excessive.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="security" label="Security" title="How We Protect Your Data">
      <p className="policy-p">
        We implement appropriate technical and organisational security measures to protect your personal information against accidental or unlawful destruction, loss, alteration, and unauthorised disclosure.
      </p>
      <ul className="policy-ul">
        <li>256-bit SSL/TLS encryption for all data in transit</li>
        <li>AES-256 encryption for sensitive data at rest</li>
        <li>Payment processing via PCI-DSS certified providers — we never store card data</li>
        <li>Regular security audits and penetration testing</li>
        <li>Strict role-based access controls — staff access only what they need</li>
        <li>Immediate breach notification procedures in compliance with applicable law</li>
      </ul>
      <Callout>
        Despite our best efforts, no security system is impenetrable. If you suspect any unauthorised activity on your account, contact us immediately at <strong>security@voleluxe.co.tz</strong>.
      </Callout>
    </Section>

    <div className="section-divider" />

    <Section id="children" label="Children" title="Children's Privacy">
      <p className="policy-p">
        Our services are not directed at individuals under the age of 16. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately and we will delete such information from our records.
      </p>
      <p className="policy-p">
        For family bookings, the booking party must be an adult (18+) who accepts responsibility for all guests, including minors, in accordance with our Terms of Service.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="contact" label="Get in Touch" title="Contact Our Privacy Team">
      <p className="policy-p">
        If you have questions, concerns, or requests relating to this Privacy Policy or our handling of your personal data, please reach out. We take all privacy enquiries seriously and aim to respond within 2 business days.
      </p>
      <ul className="policy-ul">
        <li><strong>Email:</strong> privacy@voleluxe.co.tz</li>
        <li><strong>Post:</strong> Data Protection Officer, Vole Luxe Travel & Safaris, 124 Heritage Square, Old Town</li>
        <li><strong>Response time:</strong> 2 business days for general enquiries; 30 days for formal rights requests</li>
      </ul>
      <p className="policy-p">
        If you are unhappy with our response, you have the right to lodge a complaint with your local data protection authority.
      </p>
    </Section>

  </PolicyPage>
);

export default PrivacyPolicy;